import { format } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'
import { dateBR, MONTHS } from './birthdays'
import { brandOnDark, brandPrevious, brandPreviousDark, brandRamp, brandRampDark, normalizeHex } from './brandColors'
import { calcIdade } from './calcIdade'
import { prontuarioLabel } from './prontuarioLabel'

// Regras de tela do Dashboard. Datas sempre como texto 'yyyy-MM-dd', com as contas em UTC sobre a data pura:
// new Date('yyyy-MM-dd') e toISOString trocam o dia em UTC-3.

// Abas: a mesma lista do backend (dashboard.rules.ts). A ordem é a ordem das abas na tela
export const DASHBOARD_TABS = [
  { key: 'GENERAL', url: 'general', title: 'Geral', description: 'o dia da clínica' },
  { key: 'PRONTUARIOS', url: 'prontuarios', title: 'Prontuário', description: 'análise de atendimentos' },
  { key: 'PACIENTES', url: 'pacientes', title: 'Pacientes', description: 'análise dos cadastros' },
]
export const TAB_BY_URL = Object.fromEntries(DASHBOARD_TABS.map((tab) => [tab.url, tab.key]))
export const URL_BY_TAB = Object.fromEntries(DASHBOARD_TABS.map((tab) => [tab.key, tab.url]))

// Filtros de cada aba na URL da página, com os nomes dos parâmetros da API
export const TAB_PARAMS = {
  GENERAL: [],
  PRONTUARIOS: ['start_date', 'end_date', 'tecnico_id'],
  PACIENTES: ['ativo', 'attendance', 'start_date', 'end_date'],
}

// Mesmas constantes do backend (dashboard.rules.ts)
export const DASHBOARD_TIMEZONE = 'America/Sao_Paulo'
export const MAX_PERIOD_DAYS = 366
export const MIN_COMPARE_DAYS = 7
export const COMPARABLE_SINCE = '2024-07-01' // antes disso só existe a importação
export const SYSTEM_START_MONTH = '2024-07' // mês da carga inicial dos pacientes
export const RECENT_ATTENDANCE_DAYS = 90
export const LIST_PREVIEW_SIZE = 5
export const GENERAL_REFRESH_MS = 60000

// Cores em hex (atributo SVG não aceita var() em todos os navegadores). A cor da marca é a cor do menu (Configurações > Menu,
// chega pelo useChartColors) e as cores que dependem dela (série principal, ano anterior e faixa etária) saem dela no
// brandColors.js, em chartPalette, com as regras da rampa ordinal do validador de paleta: com o roxo do Ajust, ano anterior
// #ab7fc7 (3,2:1 no branco) e ano base #680e8f (10,3:1)
const BRAND_FALLBACK = '#680e8f'

export const CHART_COLORS = {
  others: '#b9b8b0', // "Outros" nos motivos e "Não informado" no sexo (sempre com o número ao lado)
  average: '#52514e', // linha da média
  grid: '#e1e0d9',
  axis: '#c3c2b7',
  tick: '#6c757d',
  ink: '#212529',
  shade: '#f1f0ec', // fundo do fim de semana e do mês escolhido
  done: '#0ca30c', noShow: '#d03b3b', open: '#ced4da', // andamento da agenda (sempre com legenda)
  cursor: 'rgba(33, 37, 41, 0.05)', // faixa sob o mouse
  surface: '#ffffff', // fundo do cartão (separação das fatias da rosca)
}

// Tema escuro: os mesmos papéis em passos para o fundo escuro dos cartões (#212529), validados com o validador de paleta
// (--mode dark --surface "#212529"). A marca clareia até 4,5:1 (brandOnDark: #b869e5 com o roxo do Ajust) e o ano anterior
// fica entre ela e o fundo (#72488b, 2,2:1); grade, eixo e textos seguem o Bootstrap escuro; realizado e falta não mudam
export const CHART_COLORS_DARK = {
  others: '#5f5e59',
  average: '#c3c2b7',
  grid: '#343a40',
  axis: '#495057',
  tick: '#9aa2aa',
  ink: '#e9ecef',
  shade: '#2b3035',
  done: '#0ca30c', noShow: '#d03b3b', open: '#495057',
  cursor: 'rgba(255, 255, 255, 0.06)',
  surface: '#212529',
}

// Sexo: três matizes categóricos da paleta de referência, validados com --pairs all no fundo branco.
// Masculino em azul para não contrariar a leitura comum; "Não informado" é o cinza de "Outros"
export const SEX_COLORS = { FEMININO: '#eb6834', MASCULINO: '#2a78d6', INDEFINIDO: '#1baf7a', NAO_INFORMADO: '#b9b8b0' }
// Os mesmos matizes nos passos do escuro da paleta de referência (validados com --pairs all no #212529)
export const SEX_COLORS_DARK = { FEMININO: '#d95926', MASCULINO: '#3987e5', INDEFINIDO: '#199e70', NAO_INFORMADO: CHART_COLORS_DARK.others }

// Cores dos gráficos no tema 'light' ou 'dark' e na cor do menu (useChartColors, nos componentes). Série principal: a marca
// (no escuro, clareada até 4,5:1); ano anterior: um tom mais claro dela (no escuro, mais perto do fundo). Faixa etária: rampa
// ordinal no matiz da marca, do tom claro (2:1 no branco) ao da marca; marca clara: o último passo é um tom mais fechado dela,
// para os 7 passos se distinguirem. No escuro, o primeiro passo é o mais perto do fundo escuro (2:1) e o último, o mais claro
export function chartPalette(theme, brandColor = BRAND_FALLBACK) {
  if (theme === 'dark') {
    return {
      chart: { ...CHART_COLORS_DARK, primary: brandOnDark(brandColor, BRAND_FALLBACK), previous: brandPreviousDark(brandColor, BRAND_FALLBACK) },
      age: brandRampDark(brandColor, 7, BRAND_FALLBACK),
      sex: SEX_COLORS_DARK,
    }
  }
  return {
    chart: { ...CHART_COLORS, primary: normalizeHex(brandColor) || BRAND_FALLBACK, previous: brandPrevious(brandColor, BRAND_FALLBACK) },
    age: brandRamp(brandColor, 7, BRAND_FALLBACK),
    sex: SEX_COLORS,
  }
}

// O que cada indicador quer dizer: o "i" ao lado do título abre { title, text } (InfoButton). Uma chave por número ou bloco
// da tela, com a definição do plano (4.1, 4.4 e aba Pacientes). Toda chave usada nas abas precisa estar aqui (teste)
export const INDICATOR_INFO = {
  // Aba Geral
  general_scheduled: {
    title: 'Agendados hoje',
    text: 'Agendamentos de hoje de todos os doutores. Bloqueios de horário (NÃO AGENDAR e REUNIÃO) e agendamentos desmarcados (status com "Libera o horário") não contam. REALIZADO, AVALIAÇÃO e FALTA NÃO JUSTIFICADA continuam contando como realizado e falta, mesmo com essa opção.',
  },
  general_done: {
    title: 'Realizados',
    text: 'Agendamentos de hoje com status REALIZADO. AVALIAÇÃO só conta depois do horário de início, porque é marcada já no agendamento.',
  },
  general_no_show: {
    title: 'Faltas',
    text: 'Agendamentos de hoje com status FALTA NÃO JUSTIFICADA.',
  },
  general_open: {
    title: 'A atender',
    text: 'O resto do dia: agendamentos que ainda não viraram realizado nem falta, inclusive os de status que o Dashboard não reconhece.',
  },
  general_evaluations: {
    title: 'Avaliações hoje',
    text: 'Agendamentos de hoje do tipo de atendimento AVALIAÇÃO, sem contar as faltas.',
  },
  general_records_today: {
    title: 'Evoluções de hoje',
    text: 'Das visitas realizadas hoje (cada paciente conta uma vez no dia), quantas já têm atendimento lançado no prontuário com a data de hoje.',
  },
  general_by_tecnico: {
    title: 'Agenda de hoje por doutor',
    text: 'Os números da agenda de hoje separados pelo doutor do agendamento, com o andamento do dia e o próximo horário de cada um.',
  },
  general_next: {
    title: 'Próximos horários',
    text: 'Agendamentos a atender de agora até o fim do dia, em ordem de horário. “Atendimentos x/y” é o progresso do prontuário, a mesma conta do modal da agenda: com contrato, os atendimentos menos 1 sobre a soma das sessões dos contratos; sem contrato, aparece só quantos atendimentos o prontuário tem, com “sem contrato”.',
  },
  general_no_show_reschedule: {
    title: 'Faltas a reagendar',
    text: 'Pacientes ativos que faltaram (FALTA NÃO JUSTIFICADA) nos últimos 30 dias e que, depois da falta, não têm nenhum agendamento que não seja bloqueio, desmarcado ou outra falta, nem evolução lançada no prontuário. A falta de PACOTE de quem já usou todas as sessões do contrato (pacote concluído) também não entra: não há sessão a remarcar. Falta de outro tipo, como RETORNO ou AVALIAÇÃO, continua na lista. A falta mais recente vem primeiro.',
  },
  general_missing_records: {
    title: 'Sessões sem evolução no prontuário',
    text: 'Visitas dos últimos 30 dias com agendamento realizado e sem nenhum atendimento do mesmo paciente na mesma data no prontuário. Cada paciente conta uma vez por dia. As de hoje ficam no card Evoluções de hoje, porque ainda dá tempo de lançar. Quando o agendamento não tem prontuário vinculado, Abrir prontuário abre o prontuário mais recente do paciente.',
  },
  general_packages: {
    title: 'Pacotes',
    text: 'Prontuários com contrato de pacientes em atendimento (evolução nos últimos 30 dias ou agendamento marcado), pela regra do Progresso. Terminando: de 1 a 3 sessões restantes. Esgotados: o pacote foi usado por inteiro ou já passou do contrato. PACOTE sem contrato: pacientes ativos com sessões do tipo PACOTE realizadas nos últimos 30 dias e nenhum contrato cadastrado.',
  },
  general_reactivation: {
    title: 'Para reativar',
    text: 'Pacientes ativos cuja última visita (evolução ou agendamento realizado) foi entre 31 e 90 dias atrás e que não têm nada marcado na agenda. Quem está em Faltas a reagendar fica só lá. Os mais recentes vêm primeiro, porque são os mais fáceis de recuperar.',
  },
  general_birthdays: {
    title: 'Aniversariantes de hoje',
    text: 'Pacientes ativos que fazem aniversário hoje, a mesma lista da tela Aniversariantes. Quem nasceu em 29/02 aparece em 28/02 nos anos que não são bissextos. O último atendimento é a data mais recente lançada no prontuário, como na tela Aniversariantes.',
  },

  // Aba Prontuário
  prontuarios_atendimentos: {
    title: 'Atendimentos',
    text: 'Evoluções lançadas no prontuário no período, mais as sessões realizadas na agenda em que o paciente ficou sem evolução no dia (no máximo uma por paciente e dia). A comparação é com o mesmo período um ano antes; no período em andamento, só com os dias completos (até ontem), e a partir do 8º dia.',
  },
  prontuarios_pacientes: {
    title: 'Pacientes atendidos',
    text: 'Pacientes diferentes com pelo menos um atendimento no período.',
  },
  prontuarios_daily_average: {
    title: 'Média por dia',
    text: 'Atendimentos divididos pelos dias que tiveram atendimento. No período em andamento, vai até ontem.',
  },
  prontuarios_new_pacientes: {
    title: 'Pacientes novos',
    text: 'Pacientes cujo primeiro atendimento na clínica caiu no período.',
  },
  prontuarios_per_paciente: {
    title: 'Atendimentos por paciente',
    text: 'Atendimentos divididos pelos pacientes atendidos: quantas vezes, em média, cada paciente veio no período.',
  },
  prontuarios_no_show: {
    title: 'Faltas',
    text: 'Agendamentos com status FALTA NÃO JUSTIFICADA no período. O percentual é sobre os realizados mais as faltas.',
  },
  prontuarios_by_day: {
    title: 'Atendimentos por dia',
    text: 'Atendimentos de cada dia do período. A linha é a média por dia com atendimento (até ontem). Os fins de semana ficam com fundo cinza.',
  },
  // O mesmo gráfico com o período acima de 62 dias, um ponto por mês: o texto de cima (linha da média, fim de semana) não vale
  prontuarios_by_period_month: {
    title: 'Atendimentos por mês',
    text: 'Atendimentos de cada mês do período. Acima de 62 dias, o gráfico mostra um ponto por mês, sem a linha da média por dia, e os meses cortados no início ou no fim do período ficam marcados como parciais.',
  },
  prontuarios_by_month: {
    title: 'Atendimentos por mês',
    text: 'Atendimentos de cada mês do ano escolhido e do ano anterior. No mês em andamento, a barra do ano anterior mostra os mesmos dias, e o contorno tracejado mostra o mês inteiro. Os meses antes de jul/2024 só têm a importação e ficam fora da comparação.',
  },
  prontuarios_reasons: {
    title: 'Principais motivos',
    text: 'Grupos de motivos pelo título do prontuário, que é texto livre. Um título pode entrar em mais de um grupo. O percentual é sobre os pacientes com evolução no período; Outros reúne os títulos sem grupo.',
  },
  prontuarios_weekdays: {
    title: 'Média por dia da semana',
    text: 'Atendimentos de cada dia da semana divididos pelos dias daquele dia da semana que tiveram atendimento. No período em andamento, vai até ontem.',
  },
  prontuarios_by_tecnico: {
    title: 'Por doutor',
    text: 'Atendimentos de cada doutor no período. Na sessão sem evolução, vale o doutor da agenda. A participação é sobre o total da clínica, mesmo com um doutor no filtro.',
  },

  // Aba Pacientes
  pacientes_total: {
    title: 'Pacientes',
    text: `Cadastros de pacientes que entram nos filtros: situação do cadastro, atendimento nos últimos ${RECENT_ATTENDANCE_DAYS} dias e data de cadastro.`,
  },
  pacientes_recent: {
    title: `Atendidos nos últimos ${RECENT_ATTENDANCE_DAYS} dias`,
    text: `Pacientes do filtro com atendimento (evolução ou sessão realizada) nos últimos ${RECENT_ATTENDANCE_DAYS} dias.`,
  },
  pacientes_age: {
    title: 'Idade média',
    text: 'Média das idades de quem tem data de nascimento válida (até hoje e com idade de até 110 anos).',
  },
  pacientes_without_prontuario: {
    title: 'Sem prontuário',
    text: 'Pacientes do filtro sem nenhum prontuário. O número menor mostra quantos já tiveram sessão realizada na agenda: esses precisam de prontuário.',
  },
  pacientes_without_contrato: {
    title: 'Sem contrato',
    text: `Pacientes do filtro sem nenhum contrato cadastrado. O número menor mostra quantos foram atendidos nos últimos ${RECENT_ATTENDANCE_DAYS} dias.`,
  },
  pacientes_address: {
    title: 'Com endereço',
    text: 'Pacientes com cidade preenchida no cadastro (o endereço vem da busca de CEP). O percentual é sobre os pacientes do filtro.',
  },
  pacientes_registrations: {
    title: 'Cadastros por mês',
    text: 'Novos cadastros de pacientes por mês, pela data de cadastro no sistema. O sistema começou em jul/2024, e esse mês inclui quem já era paciente.',
  },
  pacientes_age_ranges: {
    title: 'Faixa etária',
    text: 'Pacientes por faixa de idade, contando só quem tem data de nascimento válida.',
  },
  pacientes_sex: {
    title: 'Sexo',
    text: 'Sexo como está no cadastro do paciente. O formulário já abre com MASCULINO marcado, então a parte masculina aparece maior do que a real.',
  },
  pacientes_cities: {
    title: 'Cidades',
    text: 'As 10 cidades com mais pacientes no cadastro.',
  },
  pacientes_neighborhoods: {
    title: 'Bairros',
    text: 'Os 10 bairros com mais pacientes, sempre com a cidade.',
  },
}

export const COMPARISON_TEXTS = {
  MIN_DAYS: 'compara a partir do dia 8',
  NOT_COMPARABLE: 'sem base comparável: antes de jul/2024 só há a importação',
  NO_BASE: 'ano anterior sem atendimento',
  ZERO_BASE: 'ano anterior: 0',
  NO_CURRENT: 'sem atendimento no período',
}

// Base zero de um card quando o ano anterior teve atendimento: o texto do próprio indicador (os outros dizem 'ano anterior: 0')
const ZERO_BASE_TEXTS = {
  total_no_show: 'ano anterior sem faltas',
  new_pacientes: 'ano anterior sem pacientes novos',
}

const MONTH_SHORT = MONTHS.map((month) => month.short.toLowerCase())
const MONTH_FULL = MONTHS.map((month) => month.full.toLowerCase())
const WEEKDAY_SHORT = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb']
const WEEKDAY_FULL = ['domingo', 'segunda-feira', 'terça-feira', 'quarta-feira', 'quinta-feira', 'sexta-feira', 'sábado']
const WEEKDAY_ORDER = [1, 2, 3, 4, 5, 6, 0]
const DAY_MS = 86400000

const nf0 = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 })
const nf1 = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })

// ---------- abas e URL ----------

// Abas liberadas, na ordem fixa. O backend já devolve todas para o administrador
export function getDashboardTabs(user) {
  const tabs = user?.parameters?.dashboard_tabs
  if (!Array.isArray(tabs)) return []
  return DASHBOARD_TABS.filter((tab) => tabs.includes(tab.key))
}

export function hasTab(tabs, key) {
  return tabs.some((tab) => tab.key === key)
}

// 403 numa aba com a página aberta: esconde a aba sem esperar o F5
export function withoutTab(user, key) {
  const tabs = user?.parameters?.dashboard_tabs
  if (!Array.isArray(tabs)) return user
  return { ...user, parameters: { ...user.parameters, dashboard_tabs: tabs.filter((tab) => tab !== key) } }
}

// '?tab=prontuarios&start_date=...' -> { tab: 'PRONTUARIOS', params: { start_date, end_date, tecnico_id } }
export function readDashboardUrl(search) {
  const query = new URLSearchParams(search || '')
  const tab = TAB_BY_URL[query.get('tab')] || null
  const params = {}
  if (tab) TAB_PARAMS[tab].forEach((name) => { params[name] = query.get(name) || '' })
  return { tab, params }
}

// Aba + filtros preenchidos, na ordem fixa dos parâmetros
export function buildDashboardQuery(tab, params = {}) {
  const query = new URLSearchParams()
  if (URL_BY_TAB[tab]) query.set('tab', URL_BY_TAB[tab])
  ;(TAB_PARAMS[tab] || []).forEach((name) => { if (params[name]) query.set(name, params[name]) })
  return query.toString()
}

// ---------- datas ----------

const toUTC = (date) => { const [year, month, day] = date.split('-').map(Number); return Date.UTC(year, month - 1, day) }
const fromUTC = (time) => new Date(time).toISOString().slice(0, 10)
const pad = (value) => String(value).padStart(2, '0')

export function isValidDate(value) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) return false
  return fromUTC(toUTC(value)) === value
}

// Hoje no fuso da clínica (o mesmo do backend), não no do navegador
export function todayString(now = new Date()) {
  return formatInTimeZone(now, DASHBOARD_TIMEZONE, 'yyyy-MM-dd')
}

export function addDays(date, days) {
  return fromUTC(toUTC(date) + (days * DAY_MS))
}

// Dias entre as datas, contando as duas
export function periodDays(start_date, end_date) {
  return Math.round((toUTC(end_date) - toUTC(start_date)) / DAY_MS) + 1
}

export function weekDay(date) {
  return new Date(toUTC(date)).getUTCDay()
}

export function lastDayOfMonth(year, month) {
  return new Date(Date.UTC(year, month, 0)).getUTCDate()
}

// '2026-08-14' -> '14/08'
export function dayMonth(date) {
  const [, month, day] = (date || '').split('-')
  return (month && day) ? `${day}/${month}` : ''
}

// '2026-08-14' -> 'Sexta-feira, 14 de agosto de 2026'
export function longDateLabel(date) {
  if (!isValidDate(date)) return ''
  const [year, month, day] = date.split('-').map(Number)
  const weekday = WEEKDAY_FULL[weekDay(date)]
  return `${weekday[0].toUpperCase()}${weekday.slice(1)}, ${day} de ${MONTH_FULL[month - 1]} de ${year}`
}

export function weekdayShort(weekday) {
  return WEEKDAY_SHORT[weekday] ?? ''
}

export function weekdayLong(weekday) {
  return WEEKDAY_FULL[weekday] ?? ''
}

// Data e hora da API ('2026-08-14T10:45:00-03:00') no fuso da clínica, qualquer que seja o do navegador
export function clinicTime(value) {
  const date = new Date(value || '')
  return isNaN(date.getTime()) ? '' : formatInTimeZone(date, DASHBOARD_TIMEZONE, 'HH:mm')
}

export function clinicDate(value) {
  const date = new Date(value || '')
  return isNaN(date.getTime()) ? '' : formatInTimeZone(date, DASHBOARD_TIMEZONE, 'yyyy-MM-dd')
}

// Próximo horário de uma lista: 'hoje 14:00' ou '17/08 09:15'
export function nextLabel(value, today) {
  const date = clinicDate(value)
  if (!date) return ''
  return `${date === today ? 'hoje' : dayMonth(date)} ${clinicTime(value)}`
}

// Idade completa na data (data de nascimento como 'yyyy-MM-dd')
export function ageOn(birth, date) {
  if (!isValidDate(birth) || !isValidDate(date)) return null
  const [birthYear, birthMonth, birthDay] = birth.split('-').map(Number)
  const [year, month, day] = date.split('-').map(Number)
  const hadBirthday = (month > birthMonth) || ((month === birthMonth) && (day >= birthDay))
  return year - birthYear - (hadBirthday ? 0 : 1)
}

// Idade por extenso na lista de aniversariantes: "42 anos", "1 ano"; sem data válida, "—"
export function ageLabel(age) {
  if (age === null || age === undefined) return '—'
  return `${age} ${age === 1 ? 'ano' : 'anos'}`
}

// ---------- período da aba Prontuário ----------

export function currentMonth(today) {
  const [year, month] = today.split('-').map(Number)
  return { year, month }
}

const monthIndex = ({ year, month }) => (year * 12) + (month - 1)
const fromMonthIndex = (index) => ({ year: Math.floor(index / 12), month: (index % 12) + 1 })

export function isFutureMonth(value, today) {
  return monthIndex(value) > monthIndex(currentMonth(today))
}

// ‹ e ›: anda um mês, com virada de ano, sem passar do mês atual
export function shiftMonth(value, delta, today) {
  const index = Math.min(monthIndex(value) + delta, monthIndex(currentMonth(today)))
  return fromMonthIndex(index)
}

export function monthRange({ year, month }) {
  return {
    start_date: `${year}-${pad(month)}-01`,
    end_date: `${year}-${pad(month)}-${pad(lastDayOfMonth(year, month))}`,
  }
}

// Período de um mês inteiro -> { year, month }; outro período -> null
export function monthFromRange(start_date, end_date) {
  if (!isValidDate(start_date) || !isValidDate(end_date)) return null
  const [year, month, day] = start_date.split('-').map(Number)
  const range = monthRange({ year, month })
  return (day === 1 && range.end_date === end_date) ? { year, month } : null
}

// 'julho de 2026'
export function monthLabel({ year, month }) {
  return `${MONTH_FULL[month - 1]} de ${year}`
}

// 'jul/2026'
export function monthShortLabel({ year, month }) {
  return `${MONTH_SHORT[month - 1]}/${year}`
}

// '2024-07' -> 'jul/24' (eixo) ou 'julho de 2024' (tooltip)
export function yearMonthLabel(value, long = false) {
  const [year, month] = (value || '').split('-').map(Number)
  if (!year || !month) return ''
  return long ? monthLabel({ year, month }) : `${MONTH_SHORT[month - 1]}/${String(year).slice(2)}`
}

export function monthShortName(month) {
  return MONTH_SHORT[month - 1] ?? ''
}

export function monthFullName(month) {
  return MONTH_FULL[month - 1] ?? ''
}

// Mesmas regras do backend: o início não passa de hoje, o fim não vem antes do início e são até 366 dias
export function validatePeriod(start_date, end_date, today) {
  if (!isValidDate(start_date) || !isValidDate(end_date))
    return { key: !isValidDate(start_date) ? 'start_date' : 'end_date', message: 'Informe o início e o fim do período.' }
  if (start_date > today)
    return { key: 'start_date', message: 'O período não pode começar no futuro.' }
  if (end_date < start_date)
    return { key: 'end_date', message: 'A data final não pode ser anterior à inicial.' }
  if (periodDays(start_date, end_date) > MAX_PERIOD_DAYS)
    return { key: 'end_date', message: `O período pode ter no máximo ${MAX_PERIOD_DAYS} dias.` }
  return null
}

// Filtros da URL -> período. Sem datas (ou com datas inválidas), vale o mês atual
export function resolveProntuariosPeriod(params, today) {
  const { start_date, end_date } = params || {}
  if ((start_date || end_date) && !validatePeriod(start_date, end_date, today))
    return { start_date, end_date, month: monthFromRange(start_date, end_date) }

  const month = currentMonth(today)
  return { ...monthRange(month), month }
}

// O ano base do mês a mês é o ano da data final (cortada em hoje)
export function baseYear(end_date, today) {
  return Number(((end_date && end_date < today) ? end_date : today).slice(0, 4))
}

// 'julho de 2026' · 'outubro de 2026 (até hoje, 02/10)' · '01/07/2026 a 15/08/2026 (46 dias)'
export function periodLabel(period, today) {
  if (period.month) {
    const label = monthLabel(period.month)
    return (period.end_date >= today && period.start_date <= today) ? `${label} (até hoje, ${dayMonth(today)})` : label
  }
  const days = periodDays(period.start_date, period.end_date)
  const until = (period.end_date > today) ? ', até hoje' : ''
  return `${dateBR(period.start_date)} a ${dateBR(period.end_date)} (${nf0.format(days)} ${days === 1 ? 'dia' : 'dias'}${until})`
}

// Seletor de ano: do primeiro ano com atendimento (first_year do mês a mês) até o ano atual
export function yearOptions(firstYear, today, selectedYear) {
  const { year: current } = currentMonth(today)
  const first = Math.min(firstYear || current, selectedYear || current, current)
  return Array.from({ length: current - first + 1 }, (_, index) => first + index)
}

// Doutores do filtro: ativos primeiro, depois os inativos com "(INATIVO)"
export function tecnicoOptions(list) {
  const byName = (a, b) => (a.nome || '').localeCompare(b.nome || '', 'pt-BR')
  const active = (list || []).filter((item) => item.ativo !== false).sort(byName)
  const inactive = (list || []).filter((item) => item.ativo === false).sort(byName)
  return [
    ...active.map((item) => ({ value: item.id, label: item.nome })),
    ...inactive.map((item) => ({ value: item.id, label: `${item.nome} (INATIVO)` })),
  ]
}

export function tecnicoLabel(item) {
  return (item?.ativo === false) ? `${item.nome} (INATIVO)` : (item?.nome || '')
}

// Doutor da URL: só um id no formato uuid (outro valor daria 400 no backend); vazio = TODOS
export function resolveTecnicoId(value) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value || '') ? value : ''
}

// ---------- números ----------

export function formatNumber(value) {
  return (value === null || value === undefined) ? '—' : nf0.format(value)
}

export function formatDecimal(value) {
  return (value === null || value === undefined) ? '—' : nf1.format(value)
}

// Percentual que já vem em % da API (9.8 -> '9,8%')
export function formatPercent(value) {
  return (value === null || value === undefined) ? '—' : `${nf1.format(value)}%`
}

// Participação: inteiro; abaixo de 1%, uma casa ('0,4%')
export function formatShare(value) {
  if (value === null || value === undefined) return '—'
  return (value > 0 && value < 1) ? `${nf1.format(value)}%` : `${nf0.format(value)}%`
}

// Parte de um total em % (null quando o total é 0)
export function percentOf(part, total) {
  return total ? (part / total) * 100 : null
}

// '+9,8%' · '−3,7%' · '0,0%'
export function formatVariation(value) {
  if (value === null || value === undefined) return ''
  const sign = (value > 0) ? '+' : (value < 0) ? '−' : ''
  return `${sign}${nf1.format(Math.abs(value))}%`
}

// Verde quando melhora; nas faltas, melhorar é cair. Neutro quando subir ou cair não é bom nem ruim por si
export function variationTone(value, { lowerIsBetter = false, neutral = false } = {}) {
  if (!value) return 'flat'
  if (neutral) return 'neutral'
  const up = value > 0
  return (lowerIsBetter ? !up : up) ? 'good' : 'bad'
}

// MIN_DAYS (período em andamento com menos de 7 dias completos): o backend passa a comparar com 7 dias completos (início + 7)
// ou quando o período fecha (fim + 1), o que vier antes. 'do dia 8' só no período que começa no dia 1 do mês
export function minDaysText(period) {
  const { start_date, end_date } = period || {}
  if (!isValidDate(start_date) || !isValidDate(end_date)) return COMPARISON_TEXTS.MIN_DAYS

  const sevenDays = addDays(start_date, MIN_COMPARE_DAYS)
  const closed = addDays(end_date, 1)
  if ((start_date.slice(8) === '01') && (sevenDays <= closed)) return COMPARISON_TEXTS.MIN_DAYS
  return `compara a partir de ${dayMonth((closed < sevenDays) ? closed : sevenDays)}`
}

// Comparação de um card com o mesmo período do ano anterior. indicator: a chave do número na API ('total_no_show');
// previousAtendimentos: os atendimentos do ano anterior no mesmo intervalo; period: { start_date, end_date } da resposta
export function kpiComparison({ indicator, status, variation, current, previous, previousAtendimentos, period, lowerIsBetter = false, neutral = false, vs = '' }) {
  if (status === 'MIN_DAYS') return { type: 'note', text: minDaysText(period) }
  if (status === 'NOT_COMPARABLE') return { type: 'note', text: COMPARISON_TEXTS.NOT_COMPARABLE }
  if ((current === null) || (current === undefined)) return { type: 'note', text: COMPARISON_TEXTS.NO_CURRENT }
  // Base zero: 'sem atendimento' só quando o ano anterior não teve nenhum; senão, só o número do card era 0
  if (!previous || (variation === null) || (variation === undefined)) {
    if (previousAtendimentos === 0) return { type: 'note', text: COMPARISON_TEXTS.NO_BASE }
    return { type: 'note', text: ZERO_BASE_TEXTS[indicator] || COMPARISON_TEXTS.ZERO_BASE }
  }

  return {
    type: 'variation',
    arrow: (variation > 0) ? '▲' : (variation < 0) ? '▼' : '=',
    text: formatVariation(variation),
    tone: variationTone(variation, { lowerIsBetter, neutral }),
    vs,
  }
}

// Intervalo comparado do ano anterior: 'vs jul/2025' · 'vs 01 a 13/08/2025' · 'vs 01/07/2025 a 15/08/2025'
export function comparisonVsLabel(comparison) {
  const start = comparison?.previous_start_date
  const end = comparison?.previous_end_date
  if (!isValidDate(start) || !isValidDate(end)) return ''

  const month = monthFromRange(start, end)
  if (month) return `vs ${monthShortLabel(month)}`
  if (start === end) return `vs ${dateBR(start)}`
  if (start.slice(0, 7) === end.slice(0, 7)) return `vs ${start.slice(8)} a ${dateBR(end).slice(0, 5)}/${end.slice(0, 4)}`
  return `vs ${dateBR(start)} a ${dateBR(end)}`
}

export function joinNames(items) {
  if (items.length <= 1) return items.join('')
  return `${items.slice(0, -1).join(', ')} e ${items[items.length - 1]}`
}

// Faixa de aviso: status fora das listas do backend
export function unmappedStatusText(list) {
  if (!Array.isArray(list) || !list.length) return ''
  return `Status sem significado no Dashboard: ${joinNames(list.map((item) => `${item.nome} (${formatNumber(item.total)})`))}.`
}

// Faixa de aviso: evoluções com data depois de hoje
export function futureAtendimentosText(total) {
  if (!total) return ''
  return (total === 1)
    ? '1 atendimento com data futura não entra na conta. Corrija a data no prontuário.'
    : `${formatNumber(total)} atendimentos com data futura não entram na conta. Corrija a data no prontuário.`
}

export function plural(total, singular, pluralText) {
  return `${formatNumber(total)} ${total === 1 ? singular : pluralText}`
}

// Cor do texto sobre a cor do status (mesma conta de brilho da Agenda)
export function statusTextColor(hex) {
  const value = (hex || '').replace(/^#/, '')
  if (!/^[0-9a-fA-F]{6}$/.test(value)) return CHART_COLORS.ink
  const [red, green, blue] = [0, 2, 4].map((index) => parseInt(value.slice(index, index + 2), 16))
  return (((red * 299) + (green * 587) + (blue * 114)) / 1000 < 128) ? '#ffffff' : '#000000'
}

// ---------- gráficos da aba Prontuário ----------

// Série por dia (ou por mês acima de 62 dias). Dias que ainda não chegaram vêm com total null
export function dailyRows(response, today) {
  const monthly = response?.granularity === 'month'
  const data = response?.data || []
  const singleMonth = data.length > 0 && data.every((item) => item.date.slice(0, 7) === data[0].date.slice(0, 7))

  return data.map((item) => {
    const future = (item.total_atendimentos === null) || (item.total_atendimentos === undefined)
    const weekday = item.week_day ?? weekDay(item.date)
    const [year, month, day] = item.date.split('-').map(Number)

    return {
      date: item.date,
      label: monthly ? yearMonthLabel(item.date.slice(0, 7)) : (singleMonth ? String(day) : dayMonth(item.date)),
      title: monthly ? monthLabel({ year, month }) : `${weekdayLong(weekday)}, ${dateBR(item.date)}`,
      total: future ? null : item.total_atendimentos,
      pacientes: future ? null : item.total_pacientes,
      weekend: !monthly && ((weekday === 0) || (weekday === 6)),
      partial: Boolean(item.partial),
      today: !monthly && (item.date === today),
      future,
      scale: monthly ? 'month' : (singleMonth ? 'day' : 'days'),
    }
  })
}

// Rótulos do eixo (undefined = todos): no celular, os dias 1, 7, 13, 19, 25 e 31; período que passa de um mês, um por semana
export function dailyTicks(rows, narrow) {
  if (!rows.length) return undefined
  const steps = { month: narrow ? 3 : 1, day: narrow ? 6 : 1, days: narrow ? 14 : 7 }
  const step = steps[rows[0].scale] || 1
  if (step === 1) return undefined
  return rows.filter((_, index) => (index % step === 0)).map((row) => row.date)
}

const isComparableMonth = (year, month) => `${year}-${pad(month)}-01` >= COMPARABLE_SINCE

// Mês a mês: no mês parcial, a barra do ano anterior mostra os mesmos dias e o resto do mês vai no contorno tracejado.
// comparedUntil: até quando o mês parcial compara (ontem); null sem nenhum dia completo no mês (dia 1; em 01/01 o
// compared_until vem null)
export function monthlyRows(response) {
  const year = response?.year
  const previousYear = response?.previous_year ?? (year - 1)
  const comparedUntil = response?.compared_until || ''

  return (response?.data || []).map((item) => {
    const partial = Boolean(item.partial)
    const previousShown = partial ? (item.previous_same_days ?? item.previous_total) : item.previous_total
    const previousRest = (partial && (item.previous_total !== null) && (item.previous_total !== undefined) && (previousShown !== null))
      ? Math.max(item.previous_total - previousShown, 0) : 0

    return {
      month: item.month,
      label: MONTH_SHORT[item.month - 1],
      base: item.total ?? null,
      future: Boolean(item.future),
      previousShown: previousShown ?? null,
      previousRest: previousRest || null,
      previousTotal: item.previous_total ?? null,
      currentSameDays: item.current_same_days ?? null,
      comparedUntil: (partial && (comparedUntil.slice(0, 7) === `${year}-${pad(item.month)}`)) ? comparedUntil : null,
      partial,
      comparable: item.comparable !== false,
      variation: item.variation_percent ?? null,
      previousFaded: !isComparableMonth(previousYear, item.month),
      baseFaded: !isComparableMonth(year, item.month),
      partialTag: partial ? 'parcial' : (item.future ? 'data futura' : null),
    }
  })
}

// Mês parcial na tabela do mês a mês: o ano base até agora (a barra, como os cards) e o número que entra na variação, até
// ontem; o ano anterior nos mesmos dias, com o mês inteiro. Sem nenhum dia completo no mês, ainda não há o que comparar
export function partialBaseText(row) {
  const sameDays = row.comparedUntil ? ` · ${formatNumber(row.currentSameDays)} até ${dayMonth(row.comparedUntil)}` : ''
  return `${formatNumber(row.base)} até agora${sameDays}`
}

export function partialPreviousText(row) {
  const month = `(mês: ${formatNumber(row.previousTotal)})`
  return row.comparedUntil ? `${formatNumber(row.previousShown)} até ${dayMonth(row.comparedUntil)} ${month}` : `— ${month}`
}

// Texto do acumulado: meses comparáveis com dado no ano base, até ontem no mês corrente
export function monthlyAccumulated(response) {
  const year = response?.year
  const previousYear = response?.previous_year ?? (year - 1)
  // 1º de janeiro: nenhum dia completo no ano, ainda não há acumulado (e não 0 × 0)
  if (response?.compared_until === null) return { text: `ainda sem dia completo em ${year}`, comparison: null }

  const rows = (response?.data || []).filter((item) => (item.comparable !== false) && (item.total !== null) && (item.total !== undefined) && !item.future)

  if (!rows.length) {
    const hasBase = isComparableMonth(previousYear, 12)
    return { text: hasBase ? `Sem atendimentos em ${year}` : `${previousYear} não tem base comparável: antes de jul/2024 só há a importação`, comparison: null }
  }

  const first = rows[0].month
  const last = rows[rows.length - 1]
  const range = (first === last.month) ? MONTH_SHORT[first - 1] : `${MONTH_SHORT[first - 1]}–${MONTH_SHORT[last.month - 1]}`
  const until = (last.partial && response.compared_until) ? ` (até ontem, ${dayMonth(response.compared_until)})` : ''
  const importNote = (response.data || []).some((item) => item.comparable === false) ? ' · antes de jul/2024 só há a importação' : ''
  const comparison = kpiComparison({
    status: (response.variation_percent === null && response.comparison?.status === 'MIN_DAYS') ? 'MIN_DAYS' : 'OK',
    variation: response.variation_percent,
    current: response.total,
    previous: response.previous_total,
    previousAtendimentos: response.previous_total,
  })

  return {
    text: `Acumulado ${range}${until}: ${formatNumber(response.total)} em ${year} × ${formatNumber(response.previous_total)} em ${previousYear}${importNote}`,
    comparison,
  }
}

// Variação de um mês no mês a mês (tooltip e tabela). O mês parcial é o mês corrente, que começa no dia 1: com MIN_DAYS,
// a regra do minDaysText dá 'compara a partir do dia 8'
export function monthVariationText(row, status) {
  if (row.base === null || row.future) return '—'
  if (!row.comparable) return 'sem base comparável'
  if (row.partial && status === 'MIN_DAYS' && row.variation === null) return COMPARISON_TEXTS.MIN_DAYS
  if (row.variation === null) return row.previousShown ? '—' : COMPARISON_TEXTS.NO_BASE
  return formatVariation(row.variation)
}

// Média por dia da semana: seg a sex, e sáb e dom só quando têm atendimento (o backend já filtra)
export function weekdayRows(weekdays) {
  return [...(weekdays || [])]
    .sort((a, b) => WEEKDAY_ORDER.indexOf(a.week_day) - WEEKDAY_ORDER.indexOf(b.week_day))
    .map((item) => ({ ...item, label: WEEKDAY_SHORT[item.week_day], title: WEEKDAY_FULL[item.week_day] }))
}

// Motivos: maiores primeiro e "Outros" sempre por último
export function reasonRows(groups) {
  const list = (groups || []).map((item) => ({
    key: item.key,
    label: item.label,
    value: item.total_pacientes,
    atendimentos: item.total_atendimentos,
    percent: item.percent_pacientes,
    muted: item.key === 'OTHERS',
    valueText: `${formatNumber(item.total_pacientes)} · ${formatNumber(item.percent_pacientes)}%`,
  }))
  return [
    ...list.filter((item) => !item.muted).sort((a, b) => (b.value - a.value) || a.label.localeCompare(b.label, 'pt-BR')),
    ...list.filter((item) => item.muted),
  ]
}

// Títulos exatos (já normalizados no backend); o vazio aparece como "(sem título)"
export function titleRows(titles, totalPacientes) {
  return (titles || []).map((item, index) => {
    const percent = percentOf(item.total_pacientes, totalPacientes)
    return {
      key: `${index}-${item.titulo}`,
      label: (item.titulo || '').trim() || '(sem título)',
      value: item.total_pacientes,
      atendimentos: item.total_atendimentos,
      percent,
      muted: false,
      valueText: (percent === null) ? formatNumber(item.total_pacientes) : `${formatNumber(item.total_pacientes)} · ${formatNumber(percent)}%`,
    }
  })
}

// ---------- aba Pacientes ----------

export const ATIVO_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'true', label: 'Ativos' },
  { value: 'false', label: 'Inativos' },
]

export const ATTENDANCE_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'RECENT', label: `Atendidos nos últimos ${RECENT_ATTENDANCE_DAYS} dias` },
  { value: 'NOT_RECENT', label: `Sem atendimento há mais de ${RECENT_ATTENDANCE_DAYS} dias` },
]

export const REGISTRATION_OPTIONS = [
  { value: 'ALL', label: 'Todo o período' },
  { value: 'THIS_YEAR', label: 'Este ano' },
  { value: 'LAST_12_MONTHS', label: 'Últimos 12 meses' },
  { value: 'CUSTOM', label: 'Personalizar' },
]

// Data de cadastro: "Este ano" e "Últimos 12 meses" (os 12 meses do calendário, com o atual) vão até agora (end_date vazio)
export function registrationRange(option, today) {
  const { year, month } = currentMonth(today)
  if (option === 'THIS_YEAR') return { start_date: `${year}-01-01`, end_date: '' }
  if (option === 'LAST_12_MONTHS') return { start_date: monthRange(shiftMonth({ year, month }, -11, today)).start_date, end_date: '' }
  return { start_date: '', end_date: '' }
}

export function registrationOption({ start_date, end_date }, today) {
  if (!start_date && !end_date) return 'ALL'
  if (!end_date && start_date === registrationRange('THIS_YEAR', today).start_date) return 'THIS_YEAR'
  if (!end_date && start_date === registrationRange('LAST_12_MONTHS', today).start_date) return 'LAST_12_MONTHS'
  return 'CUSTOM'
}

// Mesmas regras do backend, sem o limite de 366 dias: início e fim são opcionais
export function validateRegistrationPeriod(start_date, end_date, today) {
  if (start_date && !isValidDate(start_date)) return { key: 'start_date', message: 'Data inicial inválida.' }
  if (end_date && !isValidDate(end_date)) return { key: 'end_date', message: 'Data final inválida.' }
  if (start_date && start_date > today) return { key: 'start_date', message: 'O período não pode começar no futuro.' }
  if (start_date && end_date && end_date < start_date) return { key: 'end_date', message: 'A data final não pode ser anterior à inicial.' }
  return null
}

// Filtros da URL -> filtros válidos (valor desconhecido vira "todos")
export function resolvePacientesParams(params, today) {
  const ativo = ['true', 'false'].includes(params?.ativo) ? params.ativo : ''
  const attendance = ['RECENT', 'NOT_RECENT'].includes(params?.attendance) ? params.attendance : ''
  const start_date = params?.start_date || ''
  const end_date = params?.end_date || ''
  const valid = !validateRegistrationPeriod(start_date, end_date, today)
  return { ativo, attendance, start_date: valid ? start_date : '', end_date: valid ? end_date : '' }
}

export function registrationPeriodLabel({ start_date, end_date }, today) {
  const option = registrationOption({ start_date, end_date }, today)
  if (option === 'ALL') return 'Todo o período'
  if (option !== 'CUSTOM') return `${REGISTRATION_OPTIONS.find((item) => item.value === option).label} (desde ${dateBR(start_date)})`
  if (start_date && end_date) return `${dateBR(start_date)} a ${dateBR(end_date)}`
  return start_date ? `desde ${dateBR(start_date)}` : `até ${dateBR(end_date)}`
}

// Rótulos dos filtros aplicados: 'Situação: Ativos' · 'Atendimento: ...' · 'Cadastro: ...'
export function pacientesFilterLabels(params, today) {
  const filters = resolvePacientesParams(params, today)
  return [
    `Situação: ${ATIVO_OPTIONS.find((item) => item.value === filters.ativo).label}`,
    `Atendimento: ${ATTENDANCE_OPTIONS.find((item) => item.value === filters.attendance).label}`,
    `Cadastro: ${registrationPeriodLabel(filters, today)}`,
  ]
}

// O mês atual leva o rótulo 'parcial' (como no mês a mês): a barra curta do mês em andamento não é queda
export function registrationRows(registrations, today) {
  const currentKey = (today || '').slice(0, 7)
  return (registrations || []).map((item) => ({
    month: item.month,
    label: yearMonthLabel(item.month),
    title: yearMonthLabel(item.month, true),
    total: item.total,
    systemStart: item.month === SYSTEM_START_MONTH,
    partialTag: (item.month === currentKey) ? 'parcial' : null,
  }))
}

export function includesSystemStart(registrations) {
  return (registrations || []).some((item) => item.month === SYSTEM_START_MONTH)
}

export function sexRows(sex, palette = chartPalette('light')) {
  return (sex || []).map((item) => ({ ...item, color: palette.sex[item.key] || palette.chart.others }))
}

export function ageRows(ranges, palette = chartPalette('light')) {
  return (ranges || []).map((item, index) => ({ ...item, color: palette.age[Math.min(index, palette.age.length - 1)] }))
}

// Cidades: 'CRICIÚMA'; bairros: 'CENTRO · CRICIÚMA' (o bairro se repete entre cidades)
export function placeRows(list, type) {
  return (list || []).map((item, index) => {
    const label = (type === 'neighborhood') ? [item.bairro, item.cidade].filter(Boolean).join(' · ') : item.cidade
    return { key: `${index}-${label}`, label, value: item.total, muted: false, valueText: formatNumber(item.total) }
  })
}

// Notas embaixo dos gráficos de endereço
export function placesFootnotes(address, totalPacientes, type) {
  if (!address) return []
  const notes = []
  if (type === 'city') {
    const shown = (address.cities || []).reduce((sum, item) => sum + item.total, 0)
    const moreCities = (address.total_cities || 0) - (address.cities || []).length
    if (moreCities > 0) notes.push(`mais ${plural(moreCities, 'cidade', 'cidades')} com ${plural(address.total_with_city - shown, 'paciente', 'pacientes')}`)
    const withoutCity = totalPacientes - (address.total_with_city || 0)
    if (withoutCity > 0) notes.push(`${plural(withoutCity, 'paciente', 'pacientes')} sem cidade no cadastro`)
  } else {
    const shown = (address.neighborhoods || []).reduce((sum, item) => sum + item.total, 0)
    const more = (address.total_neighborhoods || 0) - (address.neighborhoods || []).length
    if (more > 0) notes.push(`mais ${plural(more, 'bairro', 'bairros')} com ${plural(address.total_with_neighborhood - shown, 'paciente', 'pacientes')}`)
    const withoutNeighborhood = totalPacientes - (address.total_with_neighborhood || 0)
    if (withoutNeighborhood > 0) notes.push(`${plural(withoutNeighborhood, 'paciente', 'pacientes')} sem bairro`)
  }
  return notes
}

// ---------- aba Geral: listas e agendamento ----------

// 'Atendimentos: 12/30': o progresso que a API já calcula, na regra do Progresso do prontuário
// Sem contrato, o "x/y" do Progresso é só o número de atendimentos (N/N) e parece um pacote concluído: aqui vira "2 atendimentos · sem contrato"
export function progressText(prontuario) {
  if (prontuario?.has_contract === false) return `${plural(prontuario.total_atendimentos ?? 0, 'atendimento', 'atendimentos')} · sem contrato`
  return prontuario?.progresso ? `Atendimentos: ${prontuario.progresso}` : ''
}

// O mesmo texto da tela Aniversariantes
export function lastAttendanceText(item) {
  return item?.last_attendance ? `Último atendimento: ${dateBR(item.last_attendance)}` : 'Nenhum atendimento encontrado'
}

// Linha de apoio de uma lista, sem as partes vazias: 'DOUTOR A · PACOTE · Atendimentos: 12/30'
export function joinDetail(...parts) {
  return parts.filter(Boolean).join(' · ')
}

// Filtro da lista da Agenda (Agendamentos.listAll) para achar o agendamento de um próximo horário: o paciente e o dia do
// horário, com os outros filtros vazios, como a Agenda manda. O fim vai até o dia seguinte porque o backend lê as datas do
// filtro no fuso do servidor (UTC em produção): só o dia perderia o horário que termina depois das 21h. O item sai pelo id
export function agendamentoSearchFilter(item) {
  const day = clinicDate(item?.start)
  return {
    tecnico_id: '',
    paciente_id: item?.paciente_id ?? '',
    tipo_atendimento_id: '',
    status_agendamento: '',
    data_agendamento_inicial: day,
    data_agendamento_final: day ? addDays(day, 1) : '',
  }
}

// Data e hora no fuso do navegador, como a Agenda formata (date-fns); data inválida vira vazio em vez de erro
const localFormat = (value, pattern) => {
  const date = new Date(value || '')
  return isNaN(date.getTime()) ? '' : format(date, pattern)
}

// Formulário do modal da Agenda (components/Modals/ModalNovoAgendamento) para um agendamento da lista, na mesma regra do
// showInfoModal de pages/agenda/index.jsx: os mesmos campos, com os mesmos valores. Sem prontuário vinculado, a lista manda {}:
// o campo fica vazio e o prontuario_id undefined, como lá
export function agendamentoFormValues(agendamento) {
  const { paciente, tecnico, tipo_atendimento, prontuario, status } = agendamento || {}

  return {
    id: agendamento?.id,
    paciente: (paciente?.nome ?? '') + calcIdade(paciente?.data_nascimento ?? ''),
    paciente_id: paciente?.id,
    tecnico: tecnico?.nome,
    tecnico_id: tecnico?.id,
    tipo_atendimento: tipo_atendimento?.descricao,
    tipo_atendimento_id: tipo_atendimento?.id,
    prontuario: prontuarioLabel(prontuario),
    prontuario_id: prontuario?.id,
    status: status?.id,
    telefone1: paciente?.telefone,
    telefone2: paciente?.telefone2,
    data_inicial: localFormat(agendamento?.data_agendamento_inicial, 'yyyy-MM-dd'),
    hora_inicial: localFormat(agendamento?.data_agendamento_inicial, 'HH:mm'),
    hora_final: localFormat(agendamento?.data_agendamento_final, 'HH:mm'),
  }
}
